<template>
    <ValidationProvider
        :mode="validationMode"
        :name="label || name || 'unknown'"
        :rules="rules"
        :vid="id"
        v-slot="{ validated, valid, errors }"
        slim
    >
        <rkm-form-group
            :description="description"
            :invalid-feedback="errors[0]"
            :state="rules ? (validated ? valid : null) : null"
            :class="{ 'm-0': !label }"
        >
            <label
                v-if="label"
                :for="id"
            >
                {{ label + (rules && rules.required ? ' *' : '') }}
            </label>

            <template v-if="tooltipText">
                <rkm-tooltip
                    :target="uniqueId('info')"
                    :placement="tooltipPosition"
                >
                    {{ tooltipText }}
                </rkm-tooltip>

                <rkm-icon
                    :id="uniqueId('info')"
                    icon="info-circle"
                    class="ml-1"
                />
            </template>

            <rkm-input-group
                :append="append"
                :large="groupLarge"
                :prepend="prepend"
                :small="groupSmall"
            >
                <template v-if="!prepend && $slots.prepend" v-slot:prepend>
                    <slot name="prepend" />
                </template>

                <div
                    class="autocomplete-group"
                    :style="cssVars"
                >
                    <b-form-input
                        v-model="inputValue"
                        v-uppercase="uppercase"
                        :disabled="disabled"
                        :id="id"
                        :placeholder="placeholder"
                        :readonly="readonly"
                        :required="required"
                        :size="size"
                        :state="rules ? (validated ? valid : null) : null"
                        autocomplete="off"
                        @update="handleUpdate"
                        @blur="handleBlur"
                        @keydown.stop="handleKeyDown"
                    />

                    <div
                        v-if="!inStandBy"
                        class="autocomplete-items"
                    >
                        <template v-if="hasItems">
                            <div
                                v-for="(item, index) in items"
                                :key="index"
                                class="autocomplete-item"
                                :class="{ 'autocomplete-item-active': index === activeItemIndex }"
                                @mouseenter="handleItemMouseEnter(index)"
                                @mouseleave="handleItemMouseLeave"
                                @click.stop="selectItem(item)"
                            >
                                <slot
                                    v-if="$scopedSlots.itemLabel"
                                    v-bind:item="item"
                                    name="itemLabel"
                                />

                                <template v-else>
                                    {{ getTextFieldValue(item) }}
                                </template>
                            </div>
                        </template>

                        <div
                            v-else
                            class="autocomplete-message"
                            v-html="message"
                        ></div>

                        <div
                            v-if="hasMoreResults"
                            class="autocomplete-load-more"
                            :class="{ 'autocomplete-item-active': activeItemIndex === loadMoreItemIndex }"
                            @mousedown.prevent
                            @mouseenter="handleItemMouseEnter(loadMoreItemIndex)"
                            @mouseleave="handleItemMouseLeave"
                            @click.stop="loadMoreItems"
                        >
                            {{ loadingMore ? loadingMoreText : loadMoreText }}
                        </div>
                    </div>
                </div>

                <template v-if="!append && $slots.append" v-slot:append>
                    <slot name="append" />
                </template>
            </rkm-input-group>
        </rkm-form-group>
    </ValidationProvider>
</template>

<script>
// Components.
import { BFormInput } from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate';

// Mixins.
import { object, stringCase } from '@/mixins'

const status = {
    STANDBY: 1,
    SEARCHING: 2,
    SEARCHED: 3,
}

export default {
    components: { BFormInput, ValidationProvider },
    computed: {
        cssVars() {

            return {
                '--max-height': `${this.resultsMaxHeight}px`
            }
        },
        inputValue: {
            get() {

                return this.value instanceof Object ? this.getTextFieldValue(this.value) : this.filter
            },
            set(value) {

                this.filter = value

                this.updateModel(null)
            }
        },
        message() {

            let message = ''

            if (this.searching) {

                message = this.searchingMessage
            }
            else if (!this.hasItems) {

                message = this.emptyItemsMessage
            }

            return message.replace('#LABEL#', `<strong>${this.filter}</strong>`)
        },
        inStandBy() {

            return this.status === this.enums.status.STANDBY
        },
        searching() {

            return this.status === this.enums.status.SEARCHING
        },
        searched() {

            return this.status === this.enums.status.SEARCHED
        },
        hasItems() {

            return this.items.length > 0
        },
        pageSize() {

            return parseInt(this.resultsLimit)
        },
        hasMoreResults() {

            return this.loadMore && this.searched && this.totalFiltered > this.offset
        },
        loadMoreItemIndex() {

            return this.hasMoreResults ? this.items.length : -1
        },
        lastNavigableIndex() {

            return this.hasMoreResults ? this.items.length : this.items.length - 1
        },
        hasSeletedItem() {

            return this.value !== null
        },
        size() {

            return this.small ? 'sm' : (this.large ? 'lg' : '')
        },
        tooltipPosition() {

            if (this.tooltipTop) {

                return 'top'
            }
            else if (this.tooltipRight) {

                return 'right'
            }
            else if (this.tooltipBottom) {

                return 'bottom'
            }
            else if (this.tooltipLeft) {

                return 'left'
            }

            return ''
        },
        id() {

            // TODO: Centralizar e a tratar melhor a string, removendo caracteres especiais, acentos, etc.
            const id = (this.label || this.name || 'unknown')
                .toLowerCase()
                .replace(/[()\\/\-<>]/gi, '')
                .replace(/[\s]/gi, '-')

            return this.uniqueId(id)
        },
        rules() {

            return this.disabled ? null : this.validations
        }
    },
    created() {

        this.status = this.enums.status.STANDBY
    },
    data: () => ({
        activeItemIndex: 0,
        bouncer: null,
        enums: { status },
        filter: '',
        isMouseOverItem: false,
        items: [],
        loadingMore: false,
        offset: 0,
        requestId: 0,
        status: null,
        totalFiltered: 0,
    }),
    methods: {
        handleUpdate(filter) {

            this.resetSearch()

            if (filter.trim().length >= parseInt(this.searchTreshold)) {

                this.delaySearch(this.search)
            }
        },
        handleBlur() {

            if (!this.isMouseOverItem) {

                if (!this.hasSeletedItem) {

                    this.filter = ''
                }

                this.resetSearch()
            }
        },
        handleKeyDown(event) {

            switch (event.key) {

                case 'Escape': {

                    this.resetSearch()

                    event.preventDefault()

                    break;
                }
                case 'ArrowDown': {

                    if (this.activeItemIndex < this.lastNavigableIndex) {

                        this.activeItemIndex++
                    }

                    event.preventDefault()

                    break;
                }
                case 'ArrowUp': {

                    if (this.activeItemIndex > 0) {

                        this.activeItemIndex--
                    }

                    event.preventDefault()

                    break;
                }
                case 'Enter': {

                    if (this.activeItemIndex === this.loadMoreItemIndex) {

                        this.loadMoreItems()
                    }
                    else if (this.items[this.activeItemIndex]) {

                        this.selectItem(this.items[this.activeItemIndex])
                    }

                    event.preventDefault()

                    break;
                }
            }
        },
        handleItemMouseEnter(index) {

            this.isMouseOverItem = true
            this.activeItemIndex = index
        },
        handleItemMouseLeave() {

            this.isMouseOverItem = false
        },
        selectItem(item) {

            this.updateModel(item)
            this.resetSearch()

            this.filter = ''

            this.$emit('select', this.clone(item))
        },
        resetSearch() {

            clearTimeout(this.bouncer)

            this.status = this.enums.status.STANDBY
            this.activeItemIndex = 0
            this.isMouseOverItem = false
            this.items = []
            this.loadingMore = false
            this.offset = 0
            this.totalFiltered = 0
        },
        delaySearch(callback) {

            this.bouncer = setTimeout(() => callback(), parseInt(this.bouncerDelay))
        },
        search(append = false) {

            if (append) {

                this.loadingMore = true
            }
            else {

                this.status = this.enums.status.SEARCHING
                this.offset = 0
                this.totalFiltered = 0
            }

            const requestId = ++this.requestId
            const filterFormatted = this.searchFilterFormatter(this.filter)

            const params = {
                ...this.requestQueryStringParams,
                draw: filterFormatted.length,
                search: {
                    value: filterFormatted,
                    regex: false,
                },
                columns: this.searchFields.map(column => ({
                    name: this.camelToSnake(column.key),
                    searchable: !!column.searchable,
                    orderable: !!column.sortable,
                    search: {
                        value: column.search || ''
                    },
                })),
                start: String(this.offset),
                length: this.pageSize,
                excludeValue: this.excludeValue,
            }

            this.repository.autocomplete(params, this.requestRouteParams)
                .then(({ data }) => {

                    if (!this.inStandBy && requestId === this.requestId) {

                        let items = this.responseDataInterceptor(data.data)
                        if (this.excludeValue && this.excludeValue.value && this.excludeValue.prop) {

                            items = items.filter(item => {
                                return !this.excludeValue.prop.every(prop => {
                                    return item[prop] === this.excludeValue.value[prop];
                                });
                            });
                        }

                        this.items = append ? this.items.concat(items) : items
                        this.offset += this.pageSize
                        this.totalFiltered = data.recordsFiltered !== undefined
                            ? data.recordsFiltered
                            : this.items.length
                        this.status = this.enums.status.SEARCHED
                    }
                })
                .finally(() => {

                    if (requestId === this.requestId) {

                        this.loadingMore = false
                    }
                })
        },
        loadMoreItems() {

            if (this.loadingMore || !this.hasMoreResults) {

                return
            }

            this.search(true)
        },
        updateModel(item) {

            this.$emit('input', this.clone(item))
        },
        getTextFieldValue(item) {
            const value = this.textField instanceof Object
                ? this.textField(item)
                : item[this.textField]

            const txt = document.createElement('textarea')
            txt.innerHTML = value ?? ''
            return txt.value
        }
    },
    mixins: [object, stringCase],
    props: {
        /*
         * Autocomplete.
         */
        bouncerDelay: { type: [String, Number], default: 500 },
        emptyItemsMessage: { type: String, default: 'Nenhum resultado para #LABEL# encontrado' },
        excludeValue: {  type: Object, default: () => ({}) }, // prop and value
        loadMore: { type: Boolean, default: false },
        loadMoreText: { type: String, default: 'Carregar mais' },
        loadingMoreText: { type: String, default: 'Carregando...' },
        repository: { type: Object, required: true },
        requestQueryStringParams: { type: Object, default: () => ({}) },
        requestRouteParams: { type: Object },
        responseDataInterceptor: { type: Function, default: value => value },
        resultsLimit: { type: [String, Number], default: 10 },
        resultsMaxHeight: { type: [String, Number], default: 400 },
        searchFields: { type: Array, required: true },
        searchFilterFormatter: { type: Function, default: value => value },
        searchTreshold: { type: [String, Number], default: 2 },
        searchingMessage: { type: String, default: 'Procurando por #LABEL#' },
        textField: { type: [String, Function], default: 'nome' },
        value: { type: Object, default: null },

        /*
         * Others.
         */
        append: { type: String, default: null },
        description: { type: String },
        disabled: { type: Boolean },
        groupLarge: { type: Boolean, default: false },
        groupSmall: { type: Boolean, default: false },
        label: { type: String },
        large: { type: Boolean, default: false },
        name: { type: String },
        placeholder: { type: String },
        prepend: { type: String, default: null },
        readonly: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        small: { type: Boolean, default: false },
        tooltipBottom: { type: Boolean },
        tooltipLeft: { type: Boolean },
        tooltipRight: { type: Boolean },
        tooltipTop: { type: Boolean },
        tooltipText: { type: String },
        uppercase: { type: Boolean },
        validationMode: { type: String, default: 'aggressive' },
        validations: { type: Object, default: null },
    },
}
</script>

<style scoped>
.autocomplete-group {
    position: relative;
}

.autocomplete-items {
    width: 100%;
    position: absolute;
    z-index: 9999;
    top: 36px;
    left: 0;
    padding: 0.25rem;
    border: 1px solid #ccd2d7;
    border-top: 1px solid #eaeaea;
    background: #FFFF;
    max-height: var(--max-height);
    overflow: auto;
}

.autocomplete-items::-webkit-scrollbar {
    width: 5px;
    background: none;
    background: transparent;
}

.autocomplete-items::-webkit-scrollbar-track {
    box-shadow: none;
    background: none;
}

.autocomplete-items::-webkit-scrollbar-thumb {
    background: #ccd2d7;
    border-radius: 10px;
}

.autocomplete-message {
    font-style: italic;
    padding: 0.25rem;
    text-align: center;
}

.autocomplete-item {
    padding: 0.5rem;
    border-radius: 3px;
}

.autocomplete-item-active {
    background: #007bff;
    cursor: pointer;
    color: #FFF;
}

.autocomplete-load-more {
    position: sticky;
    bottom: 0;
    z-index: 1;
    margin: 0.25rem -0.25rem 0;
    padding: 0.5rem;
    border-top: 1px solid #eaeaea;
    background: #FFF;
    box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.06);
    text-align: center;
    font-weight: 600;
    cursor: pointer;
    color: #007bff;
}

.autocomplete-load-more.autocomplete-item-active {
    background: #007bff;
    color: #FFF;
}

.autocomplete-item[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
}

.input-group > .autocomplete-group {
    position: relative;
    -webkit-box-flex: 1;
    -ms-flex: 1 1 auto;
    flex: 1 1 auto;
    width: 1%;
    margin-bottom: 0;
}

.input-group > .autocomplete-group:not(:last-child) > .form-control {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.input-group > .autocomplete-group:not(:first-child) > .form-control {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}
</style>